/**
 * Resolve the user's text selection inside a DocxContent rendering to
 * paraId anchors — the substrate for anchored feedback (plan WS4b).
 */

import type { FeedbackAnchors } from '@/types/report-feedback';

export type SelectionAnchors = FeedbackAnchors;

const QUOTE_MAX_CHARS = 2000;

function blockOf(node: Node | null): HTMLElement | null {
  let el: HTMLElement | null = node instanceof HTMLElement ? node : (node?.parentElement ?? null);
  while (el) {
    if (el.dataset?.pos !== undefined) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

/**
 * Read the current window selection; null when it isn't a non-empty range
 * inside a docx Content rendering. Collects every block the range spans
 * (inclusive) via their `data-pos` order and returns their paraIds.
 */
export function getSelectionAnchors(): SelectionAnchors | null {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return null;
  }
  const quote = selection.toString().trim();
  if (!quote) {
    return null;
  }

  const range = selection.getRangeAt(0);
  const startBlock = blockOf(range.startContainer);
  const endBlock = blockOf(range.endContainer);
  if (!startBlock || !endBlock) {
    return null;
  }

  const container = startBlock.closest('[class*="content"]') ?? document;
  const blocks = Array.from(container.querySelectorAll<HTMLElement>('[data-pos]'));
  const startPos = Number(startBlock.dataset.pos);
  const endPos = Number(endBlock.dataset.pos);
  const [lo, hi] = startPos <= endPos ? [startPos, endPos] : [endPos, startPos];

  const paraIds: string[] = [];
  for (const el of blocks) {
    const pos = Number(el.dataset.pos);
    if (pos >= lo && pos <= hi && el.dataset.paraId) {
      paraIds.push(el.dataset.paraId);
    }
  }

  return {
    para_ids: paraIds,
    quote: quote.slice(0, QUOTE_MAX_CHARS),
  };
}
